/**
 * Reusable SMS step-up confirmation modal.
 * Used before any sensitive money-movement action. Sends an OTP to the
 * user's verified phone via Twilio Verify, prompts for the 6-digit code,
 * then returns a short-lived `stepup_token` to the caller. The caller
 * MUST send that token in the `X-Step-Up-Token` header on the next
 * privileged request.
 *
 * Caller pattern:
 *   <StepUpModal
 *      open={open}
 *      onClose={() => setOpen(false)}
 *      action="wallet_topup"            // or "wallet_withdraw"
 *      amount={5000}
 *      title="Confirm $5,000 top-up"
 *      onVerified={(token) => api.post("/wallet/topup", body, {
 *        headers: { "X-Step-Up-Token": token }
 *      })}
 *   />
 */
import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";

export default function StepUpModal({
  open, onClose, action, amount, title, description, onVerified,
}) {
  const [phase, setPhase] = useState("send");        // send | enter | verifying
  const [code, setCode]   = useState("");
  const [masked, setMasked] = useState("");
  const [err, setErr]     = useState("");
  const sentRef = useRef(false);

  // Auto-fire the SMS when the dialog opens. Guarded with a ref so
  // re-renders don't double-send (Twilio rate-limits hard).
  useEffect(() => {
    if (!open) {
      sentRef.current = false;
      setPhase("send"); setCode(""); setErr(""); setMasked("");
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;
    (async () => {
      setErr("");
      try {
        const r = await api.post("/sms/stepup/send", { action, amount });
        setMasked(r.data.masked || "your phone");
        setPhase("enter");
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (detail?.code === "phone_required") {
          setErr("Add a verified mobile phone in Settings before moving money.");
        } else {
          setErr(formatErr(detail) || e.message || "Could not send code");
        }
        setPhase("send");
      }
    })();
  }, [open, action, amount]);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.length < 4) return;
    setPhase("verifying"); setErr("");
    try {
      const r = await api.post("/sms/stepup/verify", { action, amount, code });
      toast.success("Verified");
      onVerified?.(r.data.stepup_token);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Invalid code");
      setPhase("enter");
    }
  };

  const resend = async () => {
    setErr("");
    try {
      const r = await api.post("/sms/stepup/send", { action, amount });
      setMasked(r.data.masked || "your phone");
      toast.success("New code sent");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Could not resend");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="stepup-modal">
        <DialogHeader>
          <div className="w-10 h-10 rounded-full bg-foreground/5 grid place-items-center mb-2">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <DialogTitle data-testid="stepup-title">{title || "Confirm with SMS code"}</DialogTitle>
          <DialogDescription>
            {description || "For your security, we need to verify it's really you before we move money."}
          </DialogDescription>
        </DialogHeader>

        {phase === "send" && (
          <div className="py-6 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/60"/>
            <p className="text-sm text-foreground/60 mt-3">Sending code…</p>
            {err && <p className="text-sm text-destructive mt-3" data-testid="stepup-error">{err}</p>}
          </div>
        )}

        {(phase === "enter" || phase === "verifying") && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-[13px] text-foreground/70">
              We sent a 6-digit code to <span className="font-medium text-foreground">{masked}</span>.
              Enter it below to authorize this transaction.
            </div>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2"/>
              <Input
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123 456"
                maxLength={6}
                className="pl-9 h-12 text-center text-lg tracking-[0.4em]"
                data-testid="stepup-code"
                disabled={phase === "verifying"}
              />
            </div>
            {err && <div className="text-sm text-destructive" data-testid="stepup-error">{err}</div>}
            <Button
              type="submit"
              disabled={code.length < 6 || phase === "verifying"}
              data-testid="stepup-submit"
              className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              {phase === "verifying" ? "Verifying…" : "Confirm"}
            </Button>
            <button
              type="button"
              onClick={resend}
              data-testid="stepup-resend"
              className="w-full text-[12px] text-foreground/55 hover:text-foreground"
            >
              Didn't get it? Send a new code
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
